import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faGear } from "@fortawesome/pro-solid-svg-icons";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { AnimatePresence, m } from "framer-motion";

import { Icon } from "src/base-components/Icon";
import { MotionDropdownPanel } from "src/base-components/MotionDropdownPanel";

interface IconPickerProps {
  options: Record<string, IconProp>;
  value: string | undefined | null;
  onChange: (value: string) => void;
  placeholder?: IconProp;
  disabled?: boolean;
  dataLoc?: string;
}

const DEFAULT_PLACEHOLDER_ICON = faGear;

export const IconPicker: React.FC<IconPickerProps> = ({
  value,
  options,
  onChange,
  placeholder,
  disabled,
  dataLoc,
}) => {
  const hasSelectedIcon = !!value && value in options;
  const selectedIcon = hasSelectedIcon
    ? (options[value] as IconProp)
    : (placeholder ?? DEFAULT_PLACEHOLDER_ICON);

  return (
    <Listbox disabled={disabled} value={value} horizontal onChange={onChange}>
      {({ open }) => (
        <>
          <ListboxButton
            className="flex h-15 w-15 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white focus:outline-none data-[hover]:bg-gray-50"
            data-loc={dataLoc}
          >
            <m.div
              key={value}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -14, opacity: 0 }}
              initial={{ y: 14, opacity: 0 }}
              transition={{ duration: 0.2 }}
              layout
            >
              <Icon
                color={hasSelectedIcon ? "text-gray-600" : "text-gray-300"}
                icon={selectedIcon}
                size="2xl"
              />
            </m.div>
          </ListboxButton>

          <AnimatePresence initial={false}>
            {open && (
              <ListboxOptions
                anchor={{
                  to: "bottom",
                  gap: 6,
                }}
                as={MotionDropdownPanel}
                className="focus:outline-none"
              >
                <div className="grid grid-cols-3 gap-2 px-2">
                  {Object.entries(options).map(([key, icon], index) => (
                    <ListboxOption
                      key={key}
                      className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg data-[focus]:bg-gray-50 data-[selected]:bg-gray-100"
                      data-loc={dataLoc && `${dataLoc}-icon-${index}`}
                      value={key}
                    >
                      <Icon icon={icon} size="sm" />
                    </ListboxOption>
                  ))}
                </div>
              </ListboxOptions>
            )}
          </AnimatePresence>
        </>
      )}
    </Listbox>
  );
};
